import React, {
  MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HomeIcon from '@mui/icons-material/Home';
import classNames from 'classnames';
import Context from '../../context';
import useKeepitalRoutes from '../_hook/useKeepitalRoutes';
import SchemaUserAvatar from '../_schema/SchemaUserAvatar';
import useCookie from '../_hook/useCookie';
import { COOKIE_KEY } from '../constant/cookie';
import HeaderCountryButton from './HeaderCountryButton';
import HeaderLanguageButton from './HeaderLanguageButton';
import HeaderCurrencyButton from './HeaderCurrencyButton';

interface IProps {
  avatarOnly?: boolean;
  isOpen?: boolean;
  onOpen?: (isOpen: boolean) => void;
}

const useStyles = makeStyles({
  menuPaper: {
    minWidth: '230px',
    maxWidth: '280px',
  },
  button: {
    borderRadius: '50px',
  },
  menuItem: { height: '48px' },
});

export default function HeaderAccountButton(props: IProps) {
  const classes = useStyles();
  const { avatarOnly, isOpen, onOpen } = props;
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { user } = useContext(Context);
  const { cookies, setCookie } = useCookie([COOKIE_KEY.THEME_MODE]);
  const { t } = useTranslation();
  const { path: keepitalPath } = useKeepitalRoutes();
  const isMenuOpen = Boolean(anchorEl);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const handleProfileMenuOpen = () => {
    setAnchorEl(iconButtonRef.current);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isOpen) {
      handleProfileMenuOpen();
    } else {
      handleMenuClose();
    }
  }, [isOpen]);

  useEffect(() => {
    if (onOpen) {
      onOpen(isMenuOpen);
    }
  }, [isMenuOpen]);

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      classes={{ paper: classes.menuPaper }}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        className={classNames(
          classes.menuItem,
          'default-layout-header-keepital-button-joyride'
        )}
        component="a"
        target="_blank"
        href={keepitalPath('home')}
      >
        <ListItemIcon style={{ minWidth: '40px' }}>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText primary={`${t('to')} ${t('keepital')}`} />
      </MenuItem>
      <HeaderCountryButton useMenuItem />
      <HeaderLanguageButton useMenuItem />
      <HeaderCurrencyButton useMenuItem />
      <MenuItem
        className={classNames(
          classes.menuItem,
          'default-layout-header-account-button-switch-theme-button-joyride'
        )}
        onClick={() => {
          setCookie(
            COOKIE_KEY.THEME_MODE,
            cookies[COOKIE_KEY.THEME_MODE] === 'dark' ? 'light' : 'dark'
          );
          handleMenuClose();
        }}
      >
        <ListItemIcon style={{ minWidth: '40px' }}>
          {cookies[COOKIE_KEY.THEME_MODE] === 'dark' ? (
            <DarkModeIcon />
          ) : (
            <WbSunnyIcon />
          )}
        </ListItemIcon>
        <ListItemText primary={`${t('switch')} ${t('theme')} ${t('mode')}`} />
      </MenuItem>
    </Menu>
  );

  const userFirstName =
    user.profile && user.profile.name && user.profile.name.firstname
      ? user.profile.name.firstname
      : '';
  const userLastName =
    user.profile && user.profile.name && user.profile.name.lastname
      ? user.profile.name.lastname
      : '';
  const renderIconButton = (
    <IconButton
      ref={iconButtonRef}
      className={classes.button}
      edge="end"
      aria-label="account of current user"
      aria-controls={menuId}
      aria-haspopup="true"
      onClick={() => handleProfileMenuOpen()}
      color="inherit"
      size="large"
    >
      {user && (
        <>
          <SchemaUserAvatar user={user} width={3} height={3} />
          {!avatarOnly && (
            <>
              &nbsp;&nbsp;
              <Typography variant="body1">
                {userFirstName}
                &nbsp;
                {userLastName}
              </Typography>
              <ArrowDropDownIcon />
            </>
          )}
        </>
      )}
    </IconButton>
  );

  return (
    <>
      {renderIconButton}
      {renderMenu}
    </>
  );
}
